import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const CampaignContainer = styled.div`
  padding: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #ffffff;
  min-height: calc(100vh - 100px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.div`
  margin-bottom: 2.5rem;
  padding: 2.5rem;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 5px solid #1e293b;
  
  h1 {
    font-size: 2.2rem;
    color: #000000;
    margin-bottom: 0.5rem;
    font-weight: 600;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #4b5563;
    font-size: 1rem;
    font-weight: 500;
  }
`;

const TabButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.5rem;
  border-bottom: 2px solid #e5e7eb;
  
  button {
    padding: 0.8rem 1.6rem;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.9rem;
    color: #6b7280;
    border-bottom: 3px solid transparent;
    transition: all 0.3s ease;
    font-weight: 600;
    letter-spacing: 0.25px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    
    &.active {
      color: #1e293b;
      border-bottom-color: #1e293b;
    }
    
    &:hover {
      color: #1e293b;
    }
  }
`;

const CreateCampaignForm = styled.form`
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2.5rem;
  border: 1px solid #e5e7eb;
`;

const FormGroup = styled.div`
  margin-bottom: 1.75rem;
  
  label {
    display: block;
    margin-bottom: 0.75rem;
    font-weight: 600;
    color: #1e293b;
    font-size: 0.95rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
  
  input, textarea {
    width: 100%;
    padding: 0.9rem;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 1rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    transition: all 0.3s ease;
    background: #ffffff;
    color: #1e293b;
    
    &:focus {
      outline: none;
      border-color: #1e293b;
      box-shadow: 0 0 0 4px rgba(30, 41, 59, 0.1);
      background: #ffffff;
    }
  }
  
  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #1e293b;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
    background: #0f172a;
  }

  &:active {
    transform: translateY(0);
  }
`;

const CampaignsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2rem;
`;

const CampaignCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 5px solid #1e293b;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
  
  h3 {
    margin: 0 0 0.75rem 0;
    color: #2c3e50;
    font-size: 1.2rem;
    font-weight: 800;
  }
  
  p {
    margin: 0.75rem 0;
    color: #666;
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  .status {
    display: inline-block;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-top: 0.75rem;
    
    &.draft {
      background: #f0f4f8;
      color: #667eea;
    }
    
    &.live {
      background: #d4edda;
      color: #155724;
    }
    
    &.closed {
      background: #f8d7da;
      color: #721c24;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  
  button {
    flex: 1;
    min-width: 80px;
    padding: 0.55rem 0.9rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 600;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    
    &.primary {
      background: #1e293b;
      color: white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
        background: #0f172a;
      }
    }
    
    &.danger {
      background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(244, 67, 54, 0.25);
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(244, 67, 54, 0.35);
      }
    }
    
    &:disabled {
      background: #bdc3c7;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
`;

const LoadingState = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: #4b5563;
  font-weight: 600;
  font-size: 1.1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: #1e293b;
  font-weight: 600;
  font-size: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ErrorState = styled.div`
  background: #fee2e2;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: #991b1b;
  font-weight: 600;
  font-size: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  border: 1px solid #fecaca;
`;

const AdminCampaignDashboard = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    commodity: '',
    specification: '',
    crop: '',
    minOrderQuantity: '',
    unitOfMeasurement: 'kg',
    vendors: '',
    endDate: ''
  });

  useEffect(() => {
    if (activeTab === 'manage') {
      fetchCampaigns();
    }
  }, [activeTab]);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/campaigns', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setError(error.message);
      setCampaigns([]);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate required fields
      if (!formData.commodity?.trim()) {
        setError('Commodity is required');
        return;
      }
      if (!formData.endDate) {
        setError('End date is required');
        return;
      }
      
      // Validate end date is in future
      const endDate = new Date(formData.endDate);
      if (endDate <= new Date()) {
        setError('End date must be in the future');
        return;
      }
      
      setError(null);
      
      const requestBody = {
        commodity: formData.commodity.trim(),
        specification: formData.specification.trim() || '',
        crop: formData.crop.trim() || '',
        minOrderQuantity: parseInt(formData.minOrderQuantity) || 1,
        unitOfMeasurement: formData.unitOfMeasurement || 'kg',
        vendors: formData.vendors.trim() || '',
        endDate: endDate.toISOString()
      };
      
      const response = await fetch('/api/campaigns/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const contentType = response.headers.get('content-type');
      let result;
      
      if (contentType?.includes('application/json')) {
        result = await response.json();
      } else {
        result = { success: false, message: 'Invalid response from server' };
      }
      
      if (!response.ok) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`);
      }
      
      if (result.success) {
        setError(null);
        alert('Campaign created successfully!');
        setFormData({
          commodity: '',
          specification: '',
          crop: '',
          minOrderQuantity: '',
          unitOfMeasurement: 'kg',
          vendors: '',
          endDate: ''
        });
        fetchCampaigns();
      } else {
        setError(result.message || 'Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError(`Error: ${error.message}`);
    }
  };

  const handlePublish = async (campaignId) => {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        alert('Campaign published!');
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Error publishing campaign:', error);
    }
  };

  const handleClose = async (campaignId) => {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        alert('Campaign closed!');
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Error closing campaign:', error);
    }
  };

  return (
    <CampaignContainer>
      <Header>
        <h1>📢 Buying Campaigns Management</h1>
        <p>Create and manage bulk procurement campaigns for sellers</p>
      </Header>

      <TabButtons>
        <button 
          className={activeTab === 'create' ? 'active' : ''}
          onClick={() => setActiveTab('create')}
        >
          ➕ Create Campaign
        </button>
        <button 
          className={activeTab === 'manage' ? 'active' : ''}
          onClick={() => setActiveTab('manage')}
        >
          📋 Manage Campaigns
        </button>
      </TabButtons>

      {activeTab === 'create' && (
        <CreateCampaignForm onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <label>Commodity *</label>
              <input
                type="text"
                name="commodity"
                value={formData.commodity}
                onChange={handleInputChange}
                placeholder="e.g., Fertilizer, Seeds, Tools"
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Crop Type</label>
              <input
                type="text"
                name="crop"
                value={formData.crop}
                onChange={handleInputChange}
                placeholder="e.g., Wheat, Rice"
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>Specification</label>
            <textarea
              name="specification"
              value={formData.specification}
              onChange={handleInputChange}
              placeholder="Detailed specifications..."
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <label>Minimum Order Quantity *</label>
              <input
                type="number"
                name="minOrderQuantity"
                value={formData.minOrderQuantity}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Unit of Measurement</label>
              <input
                type="text"
                name="unitOfMeasurement"
                value={formData.unitOfMeasurement}
                onChange={handleInputChange}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>Vendors</label>
            <textarea
              name="vendors"
              value={formData.vendors}
              onChange={handleInputChange}
              placeholder="List of vendors to source from"
            />
          </FormGroup>

          <FormGroup>
            <label>Campaign End Date *</label>
            <input
              type="datetime-local"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <SubmitButton type="submit">Create Campaign</SubmitButton>
        </CreateCampaignForm>
      )}

      {activeTab === 'manage' && (
        <>
          {error && (
            <ErrorState>⚠️ Error: {error}. Please check the API connection or try refreshing.</ErrorState>
          )}
          {loading ? (
            <LoadingState>⏳ Loading campaigns...</LoadingState>
          ) : campaigns.length === 0 ? (
            <EmptyState>📋 No campaigns created yet</EmptyState>
          ) : (
            <CampaignsList>
              {campaigns.map(campaign => (
                <CampaignCard key={campaign.campaignId}>
                  <h3>📦 {campaign.commodity}</h3>
                  <p><strong>Crop:</strong> {campaign.crop || 'N/A'}</p>
                  <p><strong>Min Order Qty:</strong> {campaign.minOrderQuantity} {campaign.unitOfMeasurement}</p>
                  <p><strong>End Date:</strong> {new Date(campaign.endDate).toLocaleDateString()}</p>
                  <p><strong>Committed Qty:</strong> {campaign.totalQuantityCommitted} {campaign.unitOfMeasurement}</p>
                  
                  <span className={`status ${campaign.status.toLowerCase()}`}>
                    {campaign.status}
                  </span>

                  <ActionButtons>
                    {campaign.status === 'Draft' && (
                      <button 
                        className="primary"
                        onClick={() => handlePublish(campaign.campaignId)}
                      >
                        Go Live
                      </button>
                    )}
                    {campaign.status === 'Live' && (
                      <button 
                        className="danger"
                        onClick={() => handleClose(campaign.campaignId)}
                      >
                        Close
                      </button>
                    )}
                  </ActionButtons>
                </CampaignCard>
              ))}
            </CampaignsList>
          )}
        </>
      )}
    </CampaignContainer>
  );
};

export default AdminCampaignDashboard;
